import { IconButton, Typography } from '@forgeglobal/design-framework';
import AddIcon from '@mui/icons-material/Add';
import ClearIcon from '@mui/icons-material/Clear';
import CreateIcon from '@mui/icons-material/Create';
import {
  Beneficiary,
  BeneficiaryType,
} from '@tcs/client-portal-api/types/Beneficiary';
import Table from '@tcs/client-portal-common/components/molecules/Table';
import BaseCell from '@tcs/client-portal-common/components/molecules/Table/cells/BaseCell';
import BaseHeader from '@tcs/client-portal-common/components/molecules/Table/cells/headers/BaseHeader';
import { formatPercentage } from '@tcs/client-portal-common/utils/formatPercentage';
import { Field, useFormState } from '@tcs/common/components/atoms/Form';
import { get, uniq, values } from 'lodash';
import { useCallback, useMemo } from 'react';
import { FieldArrayRenderProps } from 'react-final-form-arrays';
import PercentageInputCell from '../../molecules/Table/cells/PercentageInputCell';
import BeneficiaryActionButton from './BeneficiaryActionButton';
import { useBeneficiariesErrors } from './forms/BeneficiariesValidators';
import useStyles from './style';

interface BeneficiariesFieldArrayProps extends FieldArrayRenderProps<
  Beneficiary,
  HTMLInputElement
> {
  accountNumber: string;
  isPrimary: boolean;
}

const BeneficiaryRowActions = ({
  rowId,
  accountNumber,
  name,
  beneficiary,
  onSubmit,
  onDelete,
  isPrimary,
}) => {
  const classes = useStyles();
  const formState = useFormState();
  const hasErrors = formState.errors?.[name]?.[rowId] !== undefined;

  return (
    <div className={classes.actions}>
      <BeneficiaryActionButton
        accountNumber={accountNumber}
        name={name}
        beneficiary={beneficiary}
        onSubmit={onSubmit}
        iconButton
        formTitle="Edit Beneficiary Information"
        isPrimary={isPrimary}>
        <CreateIcon style={{ color: hasErrors ? 'red' : undefined }} />
      </BeneficiaryActionButton>
      <IconButton size="small" onClick={onDelete}>
        <ClearIcon />
      </IconButton>
    </div>
  );
};

const BeneficiariesRowCell = ({
  rowId,
  name,
  value,
  errorKeys = [],
  isDate = false,
  isPrivate = false,
}) => {
  const formState = useFormState();

  const getHasColumnError = useCallback(
    (rowId, keys) => {
      return keys.some((key) => get(formState.errors, [name, rowId, key]));
    },
    [name, formState.errors],
  );

  const hasError = getHasColumnError(rowId, errorKeys);

  return (
    <BaseCell value={value} error={hasError} isPrivate={isDate || isPrivate} />
  );
};

const BeneficiariesFieldArray = ({
  fields,
  accountNumber,
  isPrimary,
}: BeneficiariesFieldArrayProps) => {
  const { name } = fields;

  const classes = useStyles();

  const { errors, isCommunityPropertyState } = useBeneficiariesErrors(
    accountNumber,
    name,
  );

  const formState = useFormState();

  const beneficaries = formState.values[name];

  const percentageTotal = useMemo(
    () => formatPercentage(beneficaries),
    [beneficaries],
  );

  const columns = useMemo(
    () => [
      {
        id: 'name',
        accessor: ({ beneficiaryType, firstName, lastName, entityName }) => {
          let displayName = [firstName, lastName];

          if (beneficiaryType === BeneficiaryType.Entity) {
            displayName = [entityName];
          }

          const nameParts = displayName.filter(Boolean);

          if (nameParts.length) {
            return nameParts.join(' ');
          }
        },
        header: () => <BaseHeader text="Name" />,
        cell: (ctx) => {
          return (
            <BeneficiariesRowCell
              name={name}
              rowId={ctx.row.id}
              value={ctx.cell.getValue()}
              errorKeys={['firstName', 'middleName', 'lastName', 'entityName']}
            />
          );
        },
        size: 350,
      },
      {
        id: 'relationship',
        accessor: ({ beneficiaryType, relationship, entityType }) =>
          beneficiaryType === BeneficiaryType.Entity
            ? entityType
            : relationship,
        header: () => <BaseHeader text="Relationship / Entity Type" />,
        cell: (ctx) => {
          return (
            <BeneficiariesRowCell
              name={name}
              rowId={ctx.row.id}
              value={ctx.cell.getValue()}
              errorKeys={['entityType', 'relationship']}
            />
          );
        },
        size: 200,
      },
      {
        id: 'ssn',
        accessor: ({
          beneficiaryType,
          socialSecurityNumber,
          taxIdentificationNumber,
        }) =>
          beneficiaryType === BeneficiaryType.Entity
            ? taxIdentificationNumber
            : socialSecurityNumber,
        header: () => <BaseHeader text="SSN / TIN" />,
        cell: (ctx) => {
          return (
            <BeneficiariesRowCell
              name={name}
              rowId={ctx.row.id}
              value={ctx.cell.getValue()}
              errorKeys={['socialSecurityNumber', 'taxIdentificationNumber']}
              isPrivate
            />
          );
        },
        size: 200,
      },
      {
        accessor: 'dateOfBirth',
        header: () => <BaseHeader text="Date of Birth" />,
        cell: (ctx) => {
          return (
            <BeneficiariesRowCell
              name={name}
              rowId={ctx.row.id}
              value={ctx.cell.getValue()}
              errorKeys={['dateOfBirth']}
              isDate
            />
          );
        },
        size: 200,
      },
      {
        accessor: 'percentage',
        header: () => <BaseHeader text="Allocation" align="right" />,
        cell: (ctx) => (
          <Field
            name={`${name}[${ctx.row.id}].percentage`}
            render={(props) => <PercentageInputCell {...props} />}
          />
        ),
        size: 160,
      },
      {
        id: 'actions',
        accessor: (beneficiary) => beneficiary,
        header: () => <BaseHeader text="" align="right" />,
        cell: (ctx) => {
          return (
            <BeneficiaryRowActions
              accountNumber={accountNumber}
              name={name}
              rowId={ctx.row.id}
              beneficiary={ctx.cell.getValue()}
              onSubmit={(data) => fields.update(ctx.row.id, data)}
              onDelete={() => fields.remove(ctx.row.id)}
              isPrimary={isPrimary}
            />
          );
        },
      },
    ],
    [
      name,
      isCommunityPropertyState,
      formState.values.maritalStatus,
      errors.communityPropertyStateError,
    ],
  );

  return (
    <>
      <BeneficiaryActionButton
        accountNumber={accountNumber}
        name={name}
        onSubmit={fields.push}
        startIcon={<AddIcon />}
        formTitle="Add a Beneficiary"
        isPrimary={isPrimary}>
        Add New
      </BeneficiaryActionButton>
      <Table data={fields.value} columns={columns} />
      {beneficaries?.length > 0 && (
        <div className={classes.notifications}>
          <Typography variant="body2">
            Total allocation: {percentageTotal}%
          </Typography>
          {uniq(values(errors)).map((error: string, index) => (
            <Typography
              key={`error-${name}-${index}`}
              variant="body2"
              color="red">
              {error}
            </Typography>
          ))}
        </div>
      )}
    </>
  );
};

export default BeneficiariesFieldArray;
